import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  BadgeCheck,
  Copy,
  MailCheck,
  MailPlus,
  Pencil,
  KeyRound,
  Plus,
  QrCode,
  RefreshCw,
  Search,
  ShieldCheck,
  UserRound,
  UsersRound,
  X
} from 'lucide-react';
import { useSearchParams } from '../../app/router';
import { readToken, tenantTokenKey } from '../../app/session';
import { CroatianDateInput } from '../../components/CroatianDateInput';
import { useTranslation } from '../../i18n/i18n';
import { ApiError, apiRequest } from '../../lib/api';
import { useTenantApp } from '../app/TenantAppContext';

interface User {
  id: string;
  employeeCode: string;
  email: string;
  username: string | null;
  firstName: string;
  lastName: string;
  phone: string | null;
  jobTitle: string | null;
  employmentStartDate: string | null;
  status: 'INVITED' | 'ACTIVE' | 'LOCKED' | 'DISABLED';
  locale: 'hr-HR' | 'en-US' | 'de-DE';
  roleKey: string;
  roleName: string;
  teamId: string | null;
  teamName: string | null;
  hasQr: boolean;
  invitationSentAt: string | null;
  invitationDeliveryStatus: 'NOT_SENT' | 'QUEUED' | 'SENT' | 'FAILED';
}

interface Team {
  id: string;
  name: string;
  description: string | null;
  managerId: string | null;
  managerName: string | null;
  memberCount: number;
}

interface Options {
  roles: Array<{ key: string; name: string; dataScope: string }>;
  teams: Array<{ id: string; name: string }>;
  usage: { active: number; limit: number };
}

interface CreateResult {
  userId: string;
  status: string;
  activationToken: string;
  qr: { dataUrl: string; publicId: string; version: number };
  activationUrl: string;
}

interface PermissionEditor {
  user: { id: string; name: string; roleKey: string; roleName: string };
  permissions: Array<{
    key: string;
    moduleKey: string;
    name: string;
    description: string | null;
    roleGranted: boolean;
    override: 'INHERIT' | 'ALLOW' | 'DENY';
    effective: boolean;
  }>;
}

const emptyForm = {
  employeeCode: '',
  email: '',
  username: '',
  firstName: '',
  lastName: '',
  phone: '',
  jobTitle: '',
  employmentStartDate: '',
  roleKey: 'worker',
  teamId: '',
  locale: 'hr-HR'
};

export function UsersPage() {
  const { context } = useTenantApp();
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const canManage = context.user.permissions.includes('users.manage');
  const canQr = context.user.permissions.includes('users.qr');
  const canTeams = context.user.permissions.includes('teams.manage');
  const canPermissions = context.user.permissions.includes('roles.manage');
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [options, setOptions] = useState<Options>({
    roles: [],
    teams: [],
    usage: { active: 0, limit: 0 }
  });
  const [search, setSearch] = useState(params.get('search') ?? '');
  const [tab, setTab] = useState<'people' | 'teams'>('people');
  const [form, setForm] = useState(emptyForm);
  const [modal, setModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [result, setResult] = useState<CreateResult | null>(null);
  const [qr, setQr] = useState<{ name: string; userId: string; dataUrl: string; version: number } | null>(null);
  const [teamModal, setTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [teamForm, setTeamForm] = useState({ name: '', description: '', managerId: '' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [permissionEditor, setPermissionEditor] = useState<PermissionEditor | null>(null);

  async function load() {
    setError('');
    const token = readToken(tenantTokenKey);
    try {
      const [userResponse, optionResponse, teamResponse] = await Promise.all([
        apiRequest<{ items: User[] }>('/api/v1/users', { accessToken: token }),
        apiRequest<Options>('/api/v1/users/options', { accessToken: token }),
        apiRequest<{ items: Team[] }>('/api/v1/users/teams', { accessToken: token })
      ]);
      setUsers(userResponse.items);
      setOptions(optionResponse);
      setTeams(teamResponse.items);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Zaposlenici nisu dostupni.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const visibleUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) =>
      [
        user.firstName,
        user.lastName,
        user.email,
        user.employeeCode,
        user.jobTitle,
        user.teamName
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [search, users]);

  async function saveUser(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const response = await apiRequest<CreateResult | { status: string }>(
        editingUser ? `/api/v1/users/${editingUser.id}` : '/api/v1/users', {
        method: editingUser ? 'PATCH' : 'POST',
        accessToken: readToken(tenantTokenKey),
        body: JSON.stringify(form)
      });
      if (editingUser) {
        setModal(false);
        setEditingUser(null);
      } else {
        setResult(response as CreateResult);
      }
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Korisnika nije moguće spremiti.');
    } finally {
      setBusy(false);
    }
  }

  async function showQr(user: User) {
    setError('');
    try {
      const response = await apiRequest<{ dataUrl: string; version: number }>(
        `/api/v1/users/${user.id}/qr`,
        { accessToken: readToken(tenantTokenKey) }
      );
      setQr({
        userId: user.id,
        name: `${user.firstName} ${user.lastName}`,
        dataUrl: response.dataUrl,
        version: response.version
      });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'QR nije dostupan.');
    }
  }

  async function rotateQr() {
    if (!qr || !confirm('Stari QR odmah prestaje vrijediti. Generirati novi?')) return;
    const response = await apiRequest<{ dataUrl: string; version: number }>(
      `/api/v1/users/${qr.userId}/qr/rotate`,
      {
        method: 'POST',
        accessToken: readToken(tenantTokenKey)
      }
    );
    setQr({ ...qr, dataUrl: response.dataUrl, version: response.version });
  }

  async function changeStatus(user: User) {
    const next = user.status === 'DISABLED' ? 'ACTIVE' : 'DISABLED';
    if (!confirm(`${next === 'DISABLED' ? 'Onemogućiti' : 'Aktivirati'} korisnika ${user.firstName} ${user.lastName}?`)) return;
    await apiRequest(`/api/v1/users/${user.id}/status`, {
      method: 'PATCH',
      accessToken: readToken(tenantTokenKey),
      body: JSON.stringify({ status: next })
    });
    await load();
  }

  async function saveTeam(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await apiRequest(
        editingTeam ? `/api/v1/users/teams/${editingTeam.id}` : '/api/v1/users/teams', {
        method: editingTeam ? 'PATCH' : 'POST',
        accessToken: readToken(tenantTokenKey),
        body: JSON.stringify(teamForm)
      });
      setTeamModal(false);
      setEditingTeam(null);
      setTeamForm({ name: '', description: '', managerId: '' });
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Tim nije moguće spremiti.');
    } finally {
      setBusy(false);
    }
  }

  function openNew() {
    const nextCode = `ZAP-${String(options.usage.active + 1).padStart(3, '0')}`;
    setForm({ ...emptyForm, employeeCode: nextCode });
    setResult(null);
    setEditingUser(null);
    setModal(true);
    setError('');
  }

  function openEdit(user: User) {
    setEditingUser(user);
    setResult(null);
    setForm({
      employeeCode: user.employeeCode,
      email: user.email,
      username: user.username ?? '',
      firstName: user.firstName,
      lastName: user.lastName,
      phone: user.phone ?? '',
      jobTitle: user.jobTitle ?? '',
      employmentStartDate: user.employmentStartDate?.slice(0, 10) ?? '',
      roleKey: user.roleKey,
      teamId: user.teamId ?? '',
      locale: user.locale
    });
    setModal(true);
    setError('');
  }

  function openTeam(team?: Team) {
    setEditingTeam(team ?? null);
    setTeamForm(
      team
        ? {
            name: team.name,
            description: team.description ?? '',
            managerId: team.managerId ?? ''
          }
        : { name: '', description: '', managerId: '' }
    );
    setTeamModal(true);
    setError('');
  }

  async function resendInvitation(user: User) {
    setBusy(true);
    setError('');
    try {
      const response = await apiRequest<{
        activationToken: string;
        activationUrl: string;
      }>(`/api/v1/users/${user.id}/invitation`, {
        method: 'POST',
        accessToken: readToken(tenantTokenKey)
      });
      setResult({
        userId: user.id,
        status: 'INVITED',
        activationToken: response.activationToken,
        activationUrl: response.activationUrl,
        qr: { dataUrl: '', publicId: '', version: 0 }
      });
      setEditingUser(null);
      setModal(true);
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Pozivnicu nije moguće poslati.');
    } finally {
      setBusy(false);
    }
  }

  async function openPermissions(user: User) {
    setBusy(true);
    setError('');
    try {
      const response = await apiRequest<PermissionEditor>(
        `/api/v1/users/${user.id}/permissions`,
        { accessToken: readToken(tenantTokenKey) }
      );
      setPermissionEditor(response);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Ovlasti nisu dostupne.');
    } finally {
      setBusy(false);
    }
  }

  async function savePermissions() {
    if (!permissionEditor) return;
    setBusy(true);
    setError('');
    try {
      await apiRequest(`/api/v1/users/${permissionEditor.user.id}/permissions`, {
        method: 'PUT',
        accessToken: readToken(tenantTokenKey),
        body: JSON.stringify({
          overrides: permissionEditor.permissions.map((permission) => ({
            permissionKey: permission.key,
            effect: permission.override
          }))
        })
      });
      setPermissionEditor(null);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Ovlasti nije moguće spremiti.');
    } finally {
      setBusy(false);
    }
  }

  const statusLabel = (status: User['status']) =>
    t(`employees.${status.toLowerCase()}`);

  return (
    <div className="module-page">
      <header className="module-page__hero">
        <div>
          <span className="eyebrow">CORE · LJUDI</span>
          <h1>{t('employees.title')}</h1>
          <p>{t('employees.subtitle')}</p>
        </div>
        {canManage && (
          <button className="button button--primary" data-mobile-fab onClick={openNew} aria-label="Novi zaposlenik">
            <Plus size={18} /> {t('action.newEmployee')}
          </button>
        )}
      </header>

      <section className="seat-usage">
        <div>
          <UsersRound />
          <span><strong>{options.usage.active}</strong> / {options.usage.limit}</span>
          <small>{t('employees.limit')}</small>
        </div>
        <span>
          <i style={{ width: `${Math.min(100, (options.usage.active / Math.max(1, options.usage.limit)) * 100)}%` }} />
        </span>
      </section>

      <div className="page-tabs">
        <button className={tab === 'people' ? 'is-active' : ''} onClick={() => setTab('people')}>
          <UserRound size={17} /> Zaposlenici
        </button>
        <button className={tab === 'teams' ? 'is-active' : ''} onClick={() => setTab('teams')}>
          <UsersRound size={17} /> Timovi
        </button>
      </div>

      {error && <div className="form-error">{error}</div>}

      {tab === 'people' ? (
        <section className="data-card">
          <header className="data-toolbar">
            <label className="compact-search">
              <Search size={17} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={`${t('action.search')}…`}
              />
            </label>
            <button className="icon-button" onClick={() => void load()}><RefreshCw size={17} /></button>
          </header>
          <div className="people-list">
            {visibleUsers.map((user) => (
              <article className="person-row" key={user.id} role="button" tabIndex={0} onClick={() => canManage ? openEdit(user) : undefined} onKeyDown={(event) => { if (event.key === 'Enter' && canManage) openEdit(user); }}>
                <span className="avatar avatar--soft">{user.firstName[0]}{user.lastName[0]}</span>
                <div className="person-main">
                  <strong>{user.firstName} {user.lastName}</strong>
                  <small>{user.email} · {user.employeeCode}</small>
                </div>
                <div className="person-meta">
                  <span>{user.roleName}</span>
                  <small>{user.teamName ?? 'Bez tima'}</small>
                  {user.status === 'INVITED' && (
                    <small className={`invite-state invite-state--${user.invitationDeliveryStatus.toLowerCase()}`}>
                      {user.invitationDeliveryStatus === 'SENT'
                        ? 'Pozivnica poslana e-mailom'
                        : user.invitationDeliveryStatus === 'FAILED'
                          ? 'Slanje nije uspjelo'
                          : 'Pozivnica čeka slanje'}
                    </small>
                  )}
                </div>
                <span className={`status-pill status-pill--${user.status.toLowerCase()}`}>
                  {statusLabel(user.status)}
                </span>
                <div className="row-actions">
                  {canQr && (
                    <button className="icon-button" title={t('employees.qr')} onClick={(event) => { event.stopPropagation(); void showQr(user); }}>
                      <QrCode size={18} />
                    </button>
                  )}
                  {canManage && (
                    <button className="icon-button" title="Uredi zaposlenika" onClick={(event) => { event.stopPropagation(); openEdit(user); }}>
                      <Pencil size={17} />
                    </button>
                  )}
                  {canPermissions && (
                    <button className="icon-button" title="Posebne ovlasti" onClick={(event) => { event.stopPropagation(); void openPermissions(user); }}>
                      <ShieldCheck size={18} />
                    </button>
                  )}
                  {canManage && user.status === 'INVITED' && (
                    <button className="icon-button" title="Ponovno pošalji pozivnicu" onClick={(event) => { event.stopPropagation(); void resendInvitation(user); }}>
                      <MailPlus size={18} />
                    </button>
                  )}
                  {canManage && user.id !== context.user.id && (
                    <button className="button button--ghost button--small" onClick={(event) => { event.stopPropagation(); void changeStatus(user); }}>
                      {user.status === 'DISABLED' ? 'Aktiviraj' : 'Onemogući'}
                    </button>
                  )}
                </div>
              </article>
            ))}
            {!loading && !visibleUsers.length && <div className="table-empty">{t('employees.empty')}</div>}
          </div>
        </section>
      ) : (
        <section className="data-card">
          <header className="data-toolbar">
            <div><strong>Struktura timova</strong><small>Voditelji i broj članova</small></div>
            {canTeams && (
              <button className="button button--primary button--small" onClick={() => openTeam()}>
                <Plus size={16} /> Novi tim
              </button>
            )}
          </header>
          <div className="team-grid">
            {teams.map((team) => (
              <article key={team.id}>
                <span><UsersRound /></span>
                <div>
                  <strong>{team.name}</strong>
                  <p>{team.description || 'Bez opisa'}</p>
                  <small>{team.managerName ?? 'Bez voditelja'} · {team.memberCount} članova</small>
                </div>
                {canTeams && (
                  <button className="icon-button" title="Uredi tim" onClick={() => openTeam(team)}>
                    <Pencil size={17} />
                  </button>
                )}
              </article>
            ))}
            {!teams.length && <div className="table-empty">Još nema definiranih timova.</div>}
          </div>
        </section>
      )}

      {modal && (
        <div className="modal-backdrop">
          <form className="operational-modal user-editor-modal" onSubmit={saveUser}>
            <header>
              <div>
                <span className="eyebrow">NOVI KORISNIČKI RAČUN</span>
                <h2>{result ? 'Korisnik je spreman' : editingUser ? 'Uredi zaposlenika' : t('action.newEmployee')}</h2>
              </div>
              <button type="button" className="icon-button" onClick={() => setModal(false)}><X /></button>
            </header>
            {result ? (
              <div className="creation-success">
                <span className="success-seal"><BadgeCheck /></span>
                <h3><MailCheck /> Pozivnica je pripremljena za slanje.</h3>
                <p>E-mail s gumbom za aktivaciju odlazi na upisanu adresu. Link ostaje dostupan i za ručni lokalni test.</p>
                <label>
                  <span>{t('employees.activation')}</span>
                  <code>{`${location.origin}${result.activationUrl}`}</code>
                  <button type="button" className="button button--ghost" onClick={() => void navigator.clipboard.writeText(`${location.origin}${result.activationUrl}`)}>
                    <Copy size={16} /> Kopiraj
                  </button>
                </label>
                {result.qr.dataUrl && <img className="qr-preview" src={result.qr.dataUrl} alt="QR iskaznica zaposlenika" />}
                <button type="button" className="button button--primary" onClick={() => setModal(false)}>
                  Gotovo
                </button>
              </div>
            ) : (
              <>
                <div className="modal-scroll">
                  <section className="form-section">
                    <div className="form-section__title"><span><UserRound /></span><div><h3>Osobni podaci</h3><p>Osnovni podaci za identitet zaposlenika</p></div></div>
                    <div className="form-grid">
                      <label className="field"><span>Ime</span><input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></label>
                      <label className="field"><span>Prezime</span><input required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></label>
                      <label className="field"><span>E-mail</span><input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
                      <label className="field"><span>Korisničko ime</span><input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></label>
                      <label className="field"><span>Telefon</span><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
                      <label className="field"><span>Šifra zaposlenika</span><input required value={form.employeeCode} onChange={(e) => setForm({ ...form, employeeCode: e.target.value })} /></label>
                    </div>
                  </section>
                  <section className="form-section">
                    <div className="form-section__title"><span><ShieldCheck /></span><div><h3>Posao i pristup</h3><p>Uloga određuje prava i opseg podataka</p></div></div>
                    <div className="form-grid">
                      <label className="field"><span>Radno mjesto</span><input value={form.jobTitle} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} /></label>
                      <label className="field"><span>Datum zaposlenja</span><CroatianDateInput value={form.employmentStartDate} onValueChange={(value) => setForm({ ...form, employmentStartDate: value })} ariaLabel="Datum zaposlenja" /></label>
                      <label className="field"><span>Uloga</span><select value={form.roleKey} onChange={(e) => setForm({ ...form, roleKey: e.target.value })}>{options.roles.map((role) => <option key={role.key} value={role.key}>{role.name}</option>)}</select></label>
                      <label className="field"><span>Tim</span><select value={form.teamId} onChange={(e) => setForm({ ...form, teamId: e.target.value })}><option value="">Bez tima</option>{options.teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}</select></label>
                      <label className="field"><span>Jezik</span><select value={form.locale} onChange={(e) => setForm({ ...form, locale: e.target.value })}><option value="hr-HR">Hrvatski</option><option value="en-US">English</option><option value="de-DE">Deutsch</option></select></label>
                    </div>
                  </section>
                  {error && <div className="form-error">{error}</div>}
                </div>
                <footer>
                  <button type="button" className="button button--ghost" onClick={() => setModal(false)}>{t('action.cancel')}</button>
                  <button className="button button--primary" disabled={busy}>
                    <KeyRound size={17} /> {busy ? 'Spremam…' : editingUser ? 'Spremi promjene' : 'Kreiraj račun, pošalji poziv i generiraj QR'}
                  </button>
                </footer>
              </>
            )}
          </form>
        </div>
      )}

      {qr && (
        <div className="modal-backdrop">
          <section className="qr-modal">
            <header><div><span className="eyebrow">DIGITALNA ISKAZNICA</span><h2>{qr.name}</h2></div><button className="icon-button" onClick={() => setQr(null)}><X /></button></header>
            <img src={qr.dataUrl} alt={`QR ${qr.name}`} />
            <p>Verzija {qr.version} · QR je jedinstven i šifrirano spremljen.</p>
            <div>
              <button className="button button--ghost" onClick={() => window.print()}><QrCode size={17} /> Ispiši</button>
              <button className="button button--primary" onClick={() => void rotateQr()}><RefreshCw size={17} /> Rotiraj QR</button>
            </div>
          </section>
        </div>
      )}

      {teamModal && (
        <div className="modal-backdrop">
          <form className="mini-modal" onSubmit={saveTeam}>
            <header><h2>{editingTeam ? 'Uredi tim' : 'Novi tim'}</h2><button type="button" className="icon-button" onClick={() => setTeamModal(false)}><X /></button></header>
            <label className="field"><span>Naziv tima</span><input required value={teamForm.name} onChange={(e) => setTeamForm({ ...teamForm, name: e.target.value })} /></label>
            <label className="field"><span>Opis</span><textarea value={teamForm.description} onChange={(e) => setTeamForm({ ...teamForm, description: e.target.value })} /></label>
            <label className="field"><span>Voditelj</span><select value={teamForm.managerId} onChange={(e) => setTeamForm({ ...teamForm, managerId: e.target.value })}><option value="">Bez voditelja</option>{users.filter((user) => user.status === 'ACTIVE').map((user) => <option key={user.id} value={user.id}>{user.firstName} {user.lastName}</option>)}</select></label>
            <footer><button className="button button--primary" disabled={busy}>{t('action.save')}</button></footer>
          </form>
        </div>
      )}

      {permissionEditor && (
        <div className="modal-backdrop">
          <section className="operational-modal permissions-modal">
            <header>
              <div>
                <span className="eyebrow">POSEBNE OVLASTI</span>
                <h2>{permissionEditor.user.name}</h2>
                <p>{permissionEditor.user.roleName} · promjene vrijede nakon nove prijave</p>
              </div>
              <button className="icon-button" onClick={() => setPermissionEditor(null)}><X /></button>
            </header>
            <div className="modal-scroll">
              {permissionEditor.user.roleKey === 'company_admin' && (
                <div className="form-success">Glavni administrator uvijek ima puna prava firme.</div>
              )}
              <div className="permissions-matrix">
                {[...new Set(permissionEditor.permissions.map((permission) => permission.moduleKey))].map((moduleKey) => (
                  <section key={moduleKey}>
                    <h3>{moduleKey.replaceAll('_', ' ')}</h3>
                    {permissionEditor.permissions.filter((permission) => permission.moduleKey === moduleKey).map((permission) => (
                      <label key={permission.key}>
                        <span>
                          <strong>{permission.name}</strong>
                          <small>{permission.description ?? permission.key}</small>
                        </span>
                        <em className={permission.effective ? 'is-allowed' : 'is-denied'}>
                          {permission.effective ? 'Ima pristup' : 'Nema pristup'}
                        </em>
                        <select
                          disabled={permissionEditor.user.roleKey === 'company_admin'}
                          value={permission.override}
                          onChange={(event) => {
                            const override = event.target.value as 'INHERIT' | 'ALLOW' | 'DENY';
                            setPermissionEditor({
                              ...permissionEditor,
                              permissions: permissionEditor.permissions.map((item) =>
                                item.key === permission.key
                                  ? {
                                      ...item,
                                      override,
                                      effective: override === 'ALLOW' ||
                                        (override !== 'DENY' && item.roleGranted)
                                    }
                                  : item
                              )
                            });
                          }}
                        >
                          <option value="INHERIT">Prema ulozi</option>
                          <option value="ALLOW">Dodatno dopusti</option>
                          <option value="DENY">Posebno zabrani</option>
                        </select>
                      </label>
                    ))}
                  </section>
                ))}
              </div>
              {error && <div className="form-error">{error}</div>}
            </div>
            <footer>
              <button className="button button--ghost" onClick={() => setPermissionEditor(null)}>Odustani</button>
              <button className="button button--primary" disabled={busy || permissionEditor.user.roleKey === 'company_admin'} onClick={() => void savePermissions()}>
                {busy ? 'Spremam…' : 'Spremi posebne ovlasti'}
              </button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}
